import React from "react";
import { t } from "i18next";
import {
  Grid,
  CircularProgress,
  Typography,
  Card,
  CardContent,
  Box,
} from "@mui/material";
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import { Source } from "@services/sourceManagerApi/models/Source";
import { SourceStatus } from "@services/sourceManagerApi/models/SourceStatus";
import { sourceCard } from "../../../theme/styles";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";

export interface SourcesProps {
  loading: boolean;
  sources: Source[];
  isSourceActive: (source: Source) => boolean;
  onDelete: (id: number) => void;
  onOpenRoleModal: (id: number) => void;
}

interface IconContainerProps {
  active: boolean;
  onClick: () => void;
}

const isSourceLoading = (source: Source): boolean => {
  return source.status === SourceStatus.PROCESSING || source.status === SourceStatus.SUBMITTED;
};

const getCardStyle = (source: Source, isActive: boolean) => {
  const isLoading = isSourceLoading(source);
  if (isLoading) {
    // Estilo para fuentes en estado de carga (disabled)
    return {
      ...sourceCard(false), // Siempre inactivo cuando está cargando
      backgroundColor: "#f5f5f5",
      border: "2px solid #e0e0e0",
      opacity: 0.7,
    };
  }
  return sourceCard(isActive);
};

const DeleteIconContainer: React.FC<IconContainerProps> = ({ active, onClick }) => (
  <Box
    sx={{
      borderRadius: "50%",
      position: "absolute",
      top: 8,
      right: 8,
      width: 35,
      height: 35,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      "&:hover": { cursor: "pointer" },
      backgroundColor: active ? "white" : "transparent",
    }}
    onClick={onClick}
  >
    <DeleteOutlineIcon sx={{ color: active ? "error.main" : "red", fontSize: 30 }} />
  </Box>
);

const ManageIconContainer: React.FC<IconContainerProps> = ({ active, onClick }) => (
  <Box
    sx={{
      p: 2.1,
      borderRadius: "50%",
      position: "absolute",
      top: 8,
      left: 16,
      width: 32,
      height: 32,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      "&:hover": { cursor: "pointer" },
    }}
    onClick={onClick}
  >
    <ManageAccountsIcon sx={{ color: active ? "white" : "primary.main", fontSize: 35 }} />
  </Box>
);

const DocumentIcon: React.FC<{ active: boolean }> = ({ active }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      p: 1,
      my: 2,
      height: "60px",
    }}
  >
    <InsertDriveFileIcon sx={{ fontSize: 54, color: active ? "white" : "#757575" }} />
  </Box>
);

// Nuevo componente para mostrar el spinner de carga
const LoadingIcon: React.FC = () => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      my: '10px',
      height: "60px",
    }}
  >
    <CircularProgress size={40} sx={{ color: "#9e9e9e" }} />
  </Box>
);

const FileTitle: React.FC<{ name: string; active: boolean; isLoading: boolean }> = ({ name, active, isLoading }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height: "3.6em",
      textAlign: "center",
    }}
  >
    <Typography
      variant="h6"
      sx={{
        fontSize: 14,
        fontWeight: 400,
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        textOverflow: "ellipsis",
        color: isLoading ? "#9e9e9e" : (active ? "white" : "#626262"),
        lineHeight: "1.2em",
      }}
      title={name}
    >
      {name}
    </Typography>
  </Box>
);

export const SourcesCard: React.FC<SourcesProps> = ({
    loading,
    sources,
    isSourceActive,
    onDelete,
    onOpenRoleModal,
  }) => {
  if (loading) {
    return (
      <Grid item>
        <CircularProgress size={24} sx={{ mr: 2 }} />
      </Grid>
    );
  }

  if (!loading && sources.length === 0) {
    return (
      <Grid item>
        <Typography component="h2" sx={{ fontSize: 20, fontWeight: 500 }}>
          {t('administration.sources.no_sources_upload')}
        </Typography>
      </Grid>
    );
  }

  return (
    <>
      {sources.map((source) => {
        const isActive = isSourceActive(source);
        const isLoading = isSourceLoading(source);
        
        return (
          <Grid item xs={12} sm={6} md={3} lg={3} xl={2} key={source.id}>
            <Card sx={getCardStyle(source, isActive)}>
              <CardContent
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  p: 2,
                  position: "relative",
                }}
              >

                {!isLoading && (
                  <>
                    <ManageIconContainer
                      active={isActive}
                      onClick={() => onOpenRoleModal(source.id)}
                    />

                    <DeleteIconContainer
                      active={isActive}
                      onClick={() => onDelete(source.id)}
                    />
                  </>
                )}

                {/* Mostrar spinner si está cargando, sino mostrar icono de documento */}
                {isLoading ? (
                  <>
                    <LoadingIcon />
                    <Typography fontSize={10} color={"#9e9e9e"}>Cargando documento ...</Typography>
                  </>
                ) : (
                  <DocumentIcon active={isActive} />
                )}

                <FileTitle name={source.name} active={isActive} isLoading={isLoading} />

              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </>
  );
};